/**
 * Item detail route — thin shim mounting `<ItemDetailPage />`.
 *
 * The detail page owns the sticky composer-head + 4-cell cover-grid +
 * 4 tabs. The route only provides the WorkspaceShell chrome and decodes
 * `itemId` from the URL.
 *
 * @see apps/web/src/features/items/detail/item-detail-page.tsx
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { hasPermission } from "@flint/api-contract";
import { ItemDetailPage } from "../../../../features/items/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { meQueryOptions } from "../../../../lib/session.ts";

export const Route = createFileRoute("/(workspace)/sales/items/$itemId/")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "items.view")) {
      throw notFound();
    }
  },
  // `?tab=fabric` deep-links straight to the Fabric-specs tab (from the inward board).
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === "string" ? { tab: search.tab } : {},
  component: ItemDetailRoute,
});

function ItemDetailRoute() {
  const { itemId } = Route.useParams();
  const { tab } = Route.useSearch();
  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Sales", to: "/" },
        { label: "Items", to: "/sales/items" },
        { label: "Detail" },
      ]}
      viewportClassName="p-0"
      contentClassName="max-w-none"
    >
      <ItemDetailPage itemId={itemId} initialTab={tab} />
    </WorkspaceShell>
  );
}
